import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Student } from '../types';
import { createOrGetStudent } from '../api';

interface EduContextType {
  isConnected: boolean;
  currentStudent: Student | null;
  setCurrentStudent: (student: Student | null) => void;
  loginStudent: (phone: string, name: string, email?: string) => Promise<Student | null>;
  logoutStudent: () => void;
}

const EduContext = createContext<EduContextType | undefined>(undefined);

export const EduProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(true); // Always connected since we're using localStorage
  const [currentStudent, setCurrentStudent] = useState<Student | null>(() => {
    const saved = localStorage.getItem('edu_student');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (currentStudent) {
      localStorage.setItem('edu_student', JSON.stringify(currentStudent));
    } else {
      localStorage.removeItem('edu_student');
    }
  }, [currentStudent]);

  const loginStudent = async (phone: string, name: string, email?: string) => {
    const student = await createOrGetStudent(name, phone, email);
    if (student) {
      setCurrentStudent(student);
    }
    return student;
  };

  const logoutStudent = () => {
    setCurrentStudent(null);
  };

  return (
    <EduContext.Provider
      value={{
        isConnected,
        currentStudent,
        setCurrentStudent,
        loginStudent,
        logoutStudent
      }}
    >
      {children}
    </EduContext.Provider>
  );
};

export const useEdu = () => {
  const context = useContext(EduContext);
  if (!context) throw new Error('useEdu must be used within an EduProvider');
  return context;
};